/**
 * MfaEnrollFlow — shared 3-step enrollment UX (HoD §3.1).
 *
 * Steps:
 *   1. QR + manual-entry fallback (initial render after enroll fetch).
 *   2. OtpCodeInput for the first 6-digit confirm. Hits enroll/confirm.
 *      On success, the backend mints access_token + refresh cookie and
 *      returns to the hook; we advance to step 3 in-place (do NOT
 *      navigate yet — recovery codes must be acknowledged first).
 *   3. RecoveryCodesPanel + ack checkbox. On Continue we wire the
 *      access token into the auth store, clear the mfa_pending slot,
 *      and call ``onComplete`` so the page (admin or tenant) can
 *      redirect to the right post-login surface.
 *
 * Single source of truth — the ``MfaVerifyModal`` (both admin + tenant
 * variants) and the in-Settings ``MfaSection`` consume this component with the
 * right mutation hooks injected. The HoD spec calls for one shared flow so the
 * enroll surface looks identical across hosts.
 */
import { useEffect, useState } from "react";
import type { UseMutationResult } from "@tanstack/react-query";
import { Button } from "@/components/common/Button";
import { FormField } from "@/components/common/FormField";
import { Spinner } from "@/components/common/Spinner";
import { OtpCodeInput } from "@/components/auth/OtpCodeInput";
import { QRCode } from "@/components/auth/QRCode";
import { RecoveryCodesPanel } from "@/components/auth/RecoveryCodesPanel";
import { ApiError } from "@/lib/apiError";
import { useAuthStore } from "@/stores/useAuthStore";
import type {
  MfaEnrollResponse,
} from "@/hooks/queries/auth/useMfaEnroll";
import type {
  MfaEnrollConfirmResponse,
} from "@/hooks/queries/auth/useMfaEnrollConfirm";

type EnrollMutation = UseMutationResult<MfaEnrollResponse, ApiError, void>;
type ConfirmMutation = UseMutationResult<
  MfaEnrollConfirmResponse,
  ApiError,
  { code: string }
>;

interface MfaEnrollFlowProps {
  enrollMutation: EnrollMutation;
  confirmMutation: ConfirmMutation;
  /** Called after the user acks recovery codes; parent navigates. */
  onComplete: () => void;
  /** Called when the mfa_pending TTL expired (full-card swap state). */
  onExpired: () => void;
  /** Continue-button label on step 3. Admin: "Continue to admin". */
  continueLabel?: string;
}

type Step = "loading" | "qr" | "confirm" | "recovery" | "expired" | "error";

export function MfaEnrollFlow({
  enrollMutation,
  confirmMutation,
  onComplete,
  onExpired,
  continueLabel = "Continue to dashboard",
}: MfaEnrollFlowProps): JSX.Element {
  const [step, setStep] = useState<Step>("loading");
  const [code, setCode] = useState("");
  const [enrollData, setEnrollData] = useState<MfaEnrollResponse | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  // 1. Fire enroll on mount — single round-trip to fetch secret + QR + codes.
  useEffect(() => {
    if (enrollMutation.isIdle) {
      enrollMutation.mutate(undefined, {
        onSuccess: (data) => {
          setEnrollData(data);
          setStep("qr");
        },
        onError: (err) => {
          if (err.status === 401) {
            setStep("expired");
          } else {
            // 5xx / 404 / network — surface a recoverable error card so
            // the user is not stranded on the spinner.
            setStep("error");
          }
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Holds the freshly-issued access token between step 2 (confirm
  // success) and step 3 (recovery codes acknowledged). We delay the
  // ``loginSuccess`` call until the user clicks Continue on step 3
  // because ``loginSuccess`` calls ``mfaPendingClear`` — which
  // invalidates the in-memory mfaPending slot, which the parent page
  // ``useEffect`` reads to gate the route. Calling loginSuccess
  // mid-flight redirects the user back to /login before they see the
  // recovery codes.
  const [pendingAccessToken, setPendingAccessToken] = useState<string | null>(
    null,
  );

  const handleConfirm = (): void => {
    if (code.length !== 6) return;
    setConfirmError(null);
    confirmMutation.mutate(
      { code },
      {
        onSuccess: (data) => {
          // Stash the access token in component state; commit it to the
          // auth store on Continue (step-3 ack). Until then the
          // mfaPending slot stays populated so the route gate doesn't
          // bounce.
          setPendingAccessToken(data.access_token);
          setStep("recovery");
        },
        onError: (err) => {
          if (err.status === 401 && err.error === "invalid_token") {
            setStep("expired");
          } else if (err.status === 401) {
            setConfirmError(
              "That code didn't match. Try the next one your app shows.",
            );
            setCode("");
          } else if (err.status === 429) {
            setConfirmError(
              "Too many attempts. Try again in a few minutes.",
            );
          } else {
            setConfirmError(
              "Our auth service hiccupped. Try again in a minute.",
            );
          }
        },
      },
    );
  };

  // ── Loading state ────────────────────────────────────────────────────
  if (step === "loading") {
    return (
      <div className="flex flex-col items-center gap-3 py-6">
        <Spinner size="lg" />
        <p className="text-sm text-text-secondary dark:text-brand-100/70">
          Generating your secret&hellip;
        </p>
      </div>
    );
  }

  // ── Expired state ────────────────────────────────────────────────────
  if (step === "expired" || !enrollData) {
    if (step === "error") {
      return (
        <div className="flex flex-col gap-4">
          <p className="text-sm text-text-secondary dark:text-brand-100/70">
            Our auth service hiccupped. Try again in a minute.
          </p>
          <Button type="button" className="w-full" onClick={onExpired}>
            Back to sign in
          </Button>
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-4">
        <p className="text-sm text-text-secondary dark:text-brand-100/70">
          Your sign-in attempt timed out. Sign in again to continue setup.
        </p>
        <Button type="button" className="w-full" onClick={onExpired}>
          Back to sign in
        </Button>
      </div>
    );
  }

  // ── Step 3: recovery codes ───────────────────────────────────────────
  if (step === "recovery") {
    return (
      <div className="flex flex-col gap-5">
        <p className="text-2xs font-bold uppercase tracking-[0.14em] text-brand-500/70 dark:text-brand-100/40">
          Step 3 of 3
        </p>
        <div>
          <h2 className="mb-1 text-base font-semibold text-text-primary dark:text-text-primary">
            Save your recovery codes
          </h2>
          <p className="text-sm text-text-secondary dark:text-brand-100/70">
            Use a recovery code if you lose access to your authenticator.
            Each code works once. Store them somewhere safe &mdash; you
            won&rsquo;t see them again.
          </p>
        </div>
        <RecoveryCodesPanel
          codes={enrollData.recovery_codes}
          acknowledged={acknowledged}
          onAcknowledge={setAcknowledged}
        />
        <Button
          type="button"
          className="w-full"
          disabled={!acknowledged}
          onClick={() => {
            if (pendingAccessToken) {
              useAuthStore.getState().loginSuccess(pendingAccessToken);
            }
            onComplete();
          }}
        >
          {continueLabel}
        </Button>
      </div>
    );
  }

  // ── Step 1 + 2: QR + confirm (rendered together; HoD §3.1 layout) ───
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-4">
        <p className="text-2xs font-bold uppercase tracking-[0.14em] text-brand-500/70 dark:text-brand-100/40">
          Step 1 of 2
        </p>
        <div className="flex flex-col items-start gap-4 sm:flex-row">
          <QRCode value={enrollData.provisioning_uri} />
          <div className="flex-1 space-y-2 text-sm text-text-secondary dark:text-brand-100/70">
            <p>
              Scan with Google Authenticator, 1Password, Authy, or any
              TOTP app.
            </p>
            <details>
              <summary className="cursor-pointer text-text-secondary hover:text-text-primary dark:text-brand-100/80 dark:hover:text-text-primary">
                Can&rsquo;t scan? Enter manually
              </summary>
              <div className="mt-2 space-y-1.5">
                <p className="text-xs uppercase tracking-wider text-brand-500/70 dark:text-brand-100/40">
                  Secret
                </p>
                <code className="block select-all break-all font-mono text-sm tracking-wider text-text-primary dark:text-text-primary">
                  {enrollData.totp_secret}
                </code>
                <p className="text-xs text-text-secondary dark:text-brand-100/60">
                  Issuer: CertAIn
                </p>
              </div>
            </details>
          </div>
        </div>
      </div>

      <hr className="border-brand-200 dark:border-white/10" />

      <div className="flex flex-col gap-3">
        <p className="text-2xs font-bold uppercase tracking-[0.14em] text-brand-500/70 dark:text-brand-100/40">
          Step 2 of 2
        </p>
        <FormField
          label="Enter the 6-digit code from your app"
          htmlFor="mfa-enroll-code"
          error={confirmError ?? undefined}
        >
          <OtpCodeInput
            id="mfa-enroll-code"
            value={code}
            onChange={setCode}
            onAutoSubmit={handleConfirm}
            hasError={confirmError !== null}
            disabled={confirmMutation.isPending}
            autoFocus
          />
        </FormField>
        <Button
          type="button"
          className="w-full"
          loading={confirmMutation.isPending}
          disabled={code.length !== 6}
          onClick={handleConfirm}
        >
          Continue
        </Button>
      </div>
    </div>
  );
}
